import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { operationType } from "../../../../../services/types";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { ExpandedBlock } from "../../../expanded";
import { updateNodeValue } from "../../../../../services/method";
import { AuthenticateSelect } from "../../../../auth/select";
import { TextInput } from "../../../../form/textInput";
import { Textarea } from "../../../../form/textarea";
import { Select } from "../../../../form/select";
import { selectDataType } from "../../../../../services/types";
import { webURL } from "../../../../../services/API";
import { Main, Styled } from "./style";

type WhatsAppCloudSendMessageProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

type TemplateListItem = selectDataType & {
  template?: any;
};

const extractTemplateTextExamples = (component: any): string[] => {
  const example = component?.example || {};
  const values: string[] = [];

  if (Array.isArray(example?.header_text)) {
    values.push(...example.header_text.map((item: any) => String(item || "")));
  }

  if (Array.isArray(example?.body_text)) {
    example.body_text.forEach((row: any) => {
      if (Array.isArray(row)) {
        values.push(...row.map((item: any) => String(item || "")));
      }
    });
  }

  return values.filter(Boolean);
};

const extractPlaceholders = (text: string): string[] => {
  const matches = text.match(/\{\{\d+\}\}/g) || [];
  return Array.from(new Set(matches));
};

export function WhatsAppCloudSendMessage({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: WhatsAppCloudSendMessageProps) {
  const { setNodes } = useReactFlow();
  const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [templateItems, setTemplateItems] = React.useState<TemplateListItem[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = React.useState(false);
  const [templateError, setTemplateError] = React.useState("");
  const [isTemplatePreviewExpanded, setIsTemplatePreviewExpanded] = React.useState(true);
  const messageType = data.value?.type || "text";
  const connectionId = data.value?.connectionId || data.value?.connection?._id || data.value?.connection?.id;

  React.useEffect(() => {
    if (setChildMinimize) setChildMinimize(() => setIsExpanded((p) => !p));
    if (setChildSettings) setChildSettings(() => setSettings((p) => ({ ...p, isToggle: !p.isToggle })));
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    if (!data.value?.type) {
      updateNodeValue(id, setNodes, { type: "text", templateLanguage: "en_US" });
    }
  }, [data.value?.type, id, setNodes]);

  React.useEffect(() => {
    const connectionId =
      data.value?.connectionId || data.value?.connection?._id || data.value?.connection?.id;
    if (!isExpanded || !connectionId || messageType !== "template") return;

    let isMounted = true;
    setIsLoadingTemplates(true);
    setTemplateError("");
    axios
      .get(`${webURL}/triggers/whatsapp-cloud/templates?connectionId=${connectionId}`)
      .then((response) => {
        if (!isMounted) return;
        const templates = Array.isArray(response.data?.templates) ? response.data.templates : [];
        const items: TemplateListItem[] = templates.map((template: any, index: number) => {
          const languageCode =
            template?.language ||
            template?.language_code ||
            template?.languages?.[0]?.code ||
            "en_US";
          const isActive =
            String(template?.name || "") === String(data.value?.templateName || "");
          return {
            id: template?.name || `template_${index + 1}`,
            name: `${template?.name || `Template ${index + 1}`} (${languageCode})`,
            refId: languageCode,
            isActive,
            template,
          };
        });
        setTemplateItems(items);

        const selected =
          items.find((item: TemplateListItem) => item.isActive) ||
          items.find(
            (item: TemplateListItem) =>
              String(item.id) === String(data.value?.templateName || ""),
          );
        if (selected?.refId && selected.refId !== data.value?.templateLanguage) {
          updateNodeValue(id, setNodes, {
            templateLanguage: selected.refId,
          });
        }
      })
      .catch((error: any) => {
        if (!isMounted) return;
        setTemplateItems([]);
        setTemplateError(error?.response?.data?.message || "Unable to load templates");
      })
      .finally(() => {
        if (isMounted) setIsLoadingTemplates(false);
      });

    return () => {
      isMounted = false;
    };
  }, [data.value?.connectionId, data.value?.connection?._id, data.value?.connection?.id, data.value?.templateName, isExpanded, messageType]);

  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    keyRef?: string,
  ) => keyRef && updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
  const isWarning = !connectionId || (!data.value?.to && !data.value?.phoneNumber);

  const handleSelectTemplate = (templateId: string) => {
    const nextItems = templateItems.map((item) => ({ ...item, isActive: item.id === templateId }));
    setTemplateItems(nextItems);
    setIsTemplatePreviewExpanded(true);
    const selected = nextItems.find((item) => item.id === templateId);
    updateNodeValue(id, setNodes, {
      templateName: templateId,
      templateLanguage: selected?.refId || data.value?.templateLanguage || "en_US",
    });
  };

  const selectedTemplate = React.useMemo(
    () =>
      templateItems.find((item) => item.isActive)?.template ||
      templateItems.find((item) => String(item.id) === String(data.value?.templateName || ""))?.template ||
      null,
    [data.value?.templateName, templateItems],
  );

  const selectedTemplateComponents = Array.isArray(selectedTemplate?.components)
    ? selectedTemplate.components
    : [];
  const headerComponent = selectedTemplateComponents.find(
    (component: any) => String(component?.type || "").toUpperCase() === "HEADER",
  );
  const bodyComponent = selectedTemplateComponents.find(
    (component: any) => String(component?.type || "").toUpperCase() === "BODY",
  );
  const footerComponent = selectedTemplateComponents.find(
    (component: any) => String(component?.type || "").toUpperCase() === "FOOTER",
  );
  const buttonComponents = selectedTemplateComponents.filter(
    (component: any) => String(component?.type || "").toUpperCase() === "BUTTONS",
  );
  const detectedTemplateParameters = [
    ...extractPlaceholders(String(headerComponent?.text || "")),
    ...extractPlaceholders(String(bodyComponent?.text || "")),
    ...extractPlaceholders(String(footerComponent?.text || "")),
  ].filter((item, index, array) => array.indexOf(item) === index);
  const templateExamples = [
    ...extractTemplateTextExamples(headerComponent),
    ...extractTemplateTextExamples(bodyComponent),
    ...extractTemplateTextExamples(footerComponent),
  ].filter((item, index, array) => array.indexOf(item) === index);

  const templatePlaceholder = isLoadingTemplates
    ? "Loading templates..."
    : templateItems.length
    ? "Select template"
    : templateError || "No templates found";
  const selectedTemplateLanguage =
    selectedTemplate?.language ||
    selectedTemplate?.language_code ||
    selectedTemplate?.languages?.[0]?.code ||
    data.value?.templateLanguage ||
    "en_US";

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
          </>
        ) : data.output ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
          </>
        ) : null}

        {isExpanded && (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            style={{ minWidth: "440px" }}
            isWarning={isWarning}
          >
            <Styled>
              <AuthenticateSelect
                type="whatsappCloud"
                defaultId={connectionId}
                handleAuthenticate={(type, connection) => {
                  if (type === "add") {
                    updateNodeValue(id, setNodes, {
                      connection,
                      connectionId: connection._id || connection.id,
                    });
                  } else {
                    updateNodeValue(id, setNodes, {
                      connection: null,
                      connectionId: null,
                    });
                  }
                }}
                withLine={true}
                background={data.background}
              />

              <p className="label">Message Type</p>
              <select
                className="selectField"
                value={messageType}
                onChange={(event) => handleChangeInput(event, "type")}
              >
                <option value="text">Text</option>
                <option value="template">Template</option>
                {/* <option value="media">Media</option>
                <option value="location">Location</option>
                <option value="interactive">Interactive Buttons</option> */}
              </select>

              <TextInput
                keyRef="to"
                value={data.value?.to || data.value?.phoneNumber || ""}
                placeholder="Recipient phone number, e.g. 201005307391"
                handleChange={handleChangeInput}
              />

              {(messageType === "text" || messageType === "interactive") && (
                <Textarea
                  keyRef="message"
                  value={data.value?.message || ""}
                  placeholder="Message body or {{ $block.someNode-1.output }}"
                  handleChange={handleChangeInput}
                />
              )}

              {messageType === "template" && (
                <>
                  <div className="templatePickerSection">
                    <div className="sectionHeader">
                      <p className="label">Template</p>
                      {selectedTemplate ? (
                        <span className="languageBadge">{String(selectedTemplateLanguage)}</span>
                      ) : null}
                    </div>
                    <div >
                      <Select
                        data={templateItems}
                        handleSelectItem={handleSelectTemplate}
                        isSmall={true}
                        emptyMsg={templatePlaceholder}
                      />
                    </div>
                    {/* {selectedTemplate ? (
                      <div className="templateSummary">
                        <span>{String(selectedTemplate?.name || data.value?.templateName || "")}</span>
                        <span>{String(selectedTemplate?.category || "UTILITY")}</span>
                        <span>{String(selectedTemplate?.status || "UNKNOWN")}</span>
                      </div>
                    ) : null} */}
                  </div>

                  <Textarea
                    keyRef="templateParameters"
                    value={data.value?.templateParameters || ""}
                    placeholder='Template parameters JSON array, e.g. ["yousef", "broadband installation", "2025-12-31", "10:00 AM", "2:00 PM"]'
                    handleChange={handleChangeInput}
                  />
                  {selectedTemplate ? (
                    <div className="templatePreviewCard">
                      <button
                        type="button"
                        className={`accordionHeader ${isTemplatePreviewExpanded ? "expanded" : ""}`}
                        onClick={() => setIsTemplatePreviewExpanded((prev) => !prev)}
                      >
                        <span>Template Preview</span>
                        <span className={`arrow ${isTemplatePreviewExpanded ? "expanded" : ""}`}>▼</span>
                      </button>
                      {isTemplatePreviewExpanded ? (
                        <div className="accordionContent">
                          <div className="templateMetaRow">
                            <div>
                              <span className="metaLabel">Status</span>
                              <span className="metaValue">
                                {String(selectedTemplate?.status || "UNKNOWN")}
                              </span>
                            </div>
                            <div>
                              <span className="metaLabel">Category</span>
                              <span className="metaValue">
                                {String(selectedTemplate?.category || "UTILITY")}
                              </span>
                            </div>
                            <div>
                              <span className="metaLabel">Language</span>
                              <span className="metaValue">
                                {String(selectedTemplateLanguage)}
                              </span>
                            </div>
                          </div>

                          {headerComponent?.text ? (
                            <div className="previewBlock">
                              <p className="previewLabel">Header</p>
                              <pre>{String(headerComponent.text)}</pre>
                            </div>
                          ) : null}

                          {bodyComponent?.text ? (
                            <div className="previewBlock">
                              <p className="previewLabel">Body</p>
                              <pre>{String(bodyComponent.text)}</pre>
                            </div>
                          ) : null}

                          {footerComponent?.text ? (
                            <div className="previewBlock">
                              <p className="previewLabel">Footer</p>
                              <pre>{String(footerComponent.text)}</pre>
                            </div>
                          ) : null}

                          {buttonComponents.length ? (
                            <div className="previewBlock">
                              <p className="previewLabel">Buttons</p>
                              <div className="buttonList">
                                {buttonComponents.flatMap((component: any) =>
                                  Array.isArray(component?.buttons) ? component.buttons : [],
                                ).map((button: any, index: number) => (
                                  <span key={`${button?.text || button?.type || "button"}_${index}`} className="buttonChip">
                                    {String(button?.text || button?.type || `Button ${index + 1}`)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ) : null}

                        

                         
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}

              {messageType === "media" && (
                <>
                  <select
                    className="selectField"
                    value={data.value?.mediaType || "image"}
                    onChange={(event) => handleChangeInput(event, "mediaType")}
                  >
                    <option value="image">Image</option>
                    <option value="document">Document</option>
                    <option value="audio">Audio</option>
                    <option value="video">Video</option>
                  </select>
                  <TextInput
                    keyRef="mediaUrl"
                    value={data.value?.mediaUrl || ""}
                    placeholder="Media URL"
                    handleChange={handleChangeInput}
                  />
                  <TextInput
                    keyRef="caption"
                    value={data.value?.caption || ""}
                    placeholder="Caption or filename"
                    handleChange={handleChangeInput}
                  />
                </>
              )}

              {messageType === "location" && (
                <>
                  <div className="row">
                    <TextInput
                      keyRef="latitude"
                      value={data.value?.latitude || ""}
                      placeholder="Latitude"
                      handleChange={handleChangeInput}
                    />
                    <TextInput
                      keyRef="longitude"
                      value={data.value?.longitude || ""}
                      placeholder="Longitude"
                      handleChange={handleChangeInput}
                    />
                  </div>
                  <TextInput
                    keyRef="locationName"
                    value={data.value?.locationName || ""}
                    placeholder="Location name"
                    handleChange={handleChangeInput}
                  />
                  <TextInput
                    keyRef="address"
                    value={data.value?.address || ""}
                    placeholder="Address"
                    handleChange={handleChangeInput}
                  />
                </>
              )}

              {messageType === "interactive" && (
                <Textarea
                  keyRef="buttons"
                  value={data.value?.buttons || ""}
                  placeholder='Buttons JSON array, e.g. [{"id":"yes","title":"Yes"},{"id":"no","title":"No"}]'
                  handleChange={handleChangeInput}
                />
              )}
            </Styled>
          </ExpandedBlock>
        )}
      </Main>
    </>
  );
}
